import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/crm/whatsapp`;

export function templateKey(template) {
  return `${template.name}::${template.language}`;
}

export function inspectTemplate(template) {
  const components = template?.components || [];
  const unsupported = [];
  let body = "";
  let headerFormat = null;
  components.forEach((component) => {
    const kind = String(component.type || "").toUpperCase();
    if (kind === "BODY") body = component.text || "";
    else if (kind === "HEADER") {
      const format = String(component.format || "TEXT").toUpperCase();
      headerFormat = format;
      if (format === "TEXT" && String(component.text || "").includes("{{")) {
        unsupported.push("variabili nell'intestazione");
      } else if (["VIDEO", "GIF"].includes(format)) {
        unsupported.push("intestazione video");
      } else if (!["TEXT", "IMAGE", "DOCUMENT"].includes(format)) {
        unsupported.push(`intestazione ${format.toLowerCase()}`);
      }
    } else if (kind === "CAROUSEL") unsupported.push("carosello");
    else if (kind === "BUTTONS") {
      (component.buttons || []).forEach((button) => {
        const buttonType = String(button.type || "").toUpperCase();
        const dynamic = String(button.url || button.text || "").includes("{{");
        if (["URL", "COPY_CODE", "FLOW"].includes(buttonType) && dynamic) unsupported.push("pulsante con variabile");
        else if (buttonType === "FLOW") unsupported.push("pulsante flow");
      });
    }
  });
  const variables = [...new Set((body.match(/\{\{(\d+)\}\}/g) || []).map((item) => Number(item.replace(/\D/g, ""))))].sort((a, b) => a - b);
  const examples = ((components.find((item) => String(item.type).toUpperCase() === "BODY") || {}).example || {}).body_text;
  return {
    body,
    variables,
    unsupported: [...new Set(unsupported)],
    examples: Array.isArray(examples?.[0]) ? examples[0].map(String) : [],
    headerFormat,
  };
}

export default function WhatsAppProvaInvio({ headers, templates, fixedTemplate, onSent }) {
  const [loaded, setLoaded] = useState(templates || []);

  const load = useCallback(async () => {
    if (templates) return;
    const res = await fetch(`${BASE}/templates`, { headers });
    const json = await res.json().catch(() => ({}));
    if (res.ok) setLoaded(json.templates || []);
  }, [headers, templates]);

  useEffect(() => { setLoaded(templates || []); }, [templates]);
  useEffect(() => { load(); }, [load]);

  const approved = useMemo(
    () => (loaded || []).filter((item) => item.status === "APPROVED"),
    [loaded],
  );
  const [key, setKey] = useState("");
  const [to, setTo] = useState("");
  const [values, setValues] = useState({});
  const [headerImage, setHeaderImage] = useState(null);
  const [sending, setSending] = useState(false);

  const selected = approved.find((item) => templateKey(item) === (fixedTemplate ? templateKey(fixedTemplate) : key)) || null;
  const info = inspectTemplate(selected);

  useEffect(() => {
    if (!selected) return;
    const next = {};
    info.variables.forEach((number, index) => {
      next[number] = info.examples[index] || "";
    });
    setValues(next);
    setHeaderImage(null);
  }, [selected?.name, selected?.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!selected) return toast.error("Scegli un template approvato.");
    if (info.unsupported.length) return toast.error(info.unsupported.join(", "));
    if (!to.trim()) return toast.error("Indica il numero WhatsApp.");
    if (info.variables.some((number) => !String(values[number] || "").trim())) {
      return toast.error("Compila tutte le variabili.");
    }
    setSending(true);
    try {
      const form = new FormData();
      form.append("to", to.trim());
      form.append("template_name", selected.name);
      form.append("language", selected.language || "it");
      form.append(
        "body_parameters",
        JSON.stringify(info.variables.map((number) => String(values[number] || "").trim())),
      );
      if (info.headerFormat === "IMAGE" && headerImage) {
        form.append("header_image", headerImage);
      }
      const authHeaders = {};
      if (headers?.Authorization) authHeaders.Authorization = headers.Authorization;
      const res = await fetch(`${BASE}/invio-prova`, {
        method: "POST",
        headers: authHeaders,
        body: form,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Invio non riuscito");
      toast.success("Messaggio inviato. Lo trovi in Conversazioni.");
      if (onSent) onSent(json.message?.conversation_key || "");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3 text-sm">
      <p className="text-slate-600">
        Il destinatario, con il numero di test Meta, deve essere uno dei numeri già verificati nell'app.
        I template con intestazione immagine o PDF partono da questa prova: senza file viene usata un'immagine o un PDF di esempio.
        Restano esclusi caroselli e intestazioni video.
      </p>
      {!fixedTemplate && (
        <div>
          <Label>Template approvato</Label>
          <Select value={key} onValueChange={setKey}>
            <SelectTrigger><SelectValue placeholder="Scegli un template" /></SelectTrigger>
            <SelectContent>
              {approved.map((item) => (
                <SelectItem key={templateKey(item)} value={templateKey(item)}>
                  {item.name} · {item.language}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {selected && (
        <>
          <p className="rounded bg-slate-50 p-3 text-slate-700">{info.body || "Template senza testo."}</p>
          {info.headerFormat === "IMAGE" && (
            <div>
              <Label>Immagine di intestazione (opzionale)</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                onChange={(event) => setHeaderImage(event.target.files?.[0] || null)}
              />
              <p className="mt-1 text-xs text-slate-500">
                Se non carichi un file, Affidya usa il logo pubblico Affidya come intestazione.
              </p>
            </div>
          )}
          {info.headerFormat === "DOCUMENT" && (
            <p className="rounded border border-slate-200 bg-slate-50 p-3 text-slate-700">
              Questo template ha un PDF in intestazione: verrà allegato un documento di esempio.
            </p>
          )}
          {info.unsupported.length > 0 && (
            <p className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900">
              Non inviabile da qui: {info.unsupported.join(", ")}.
            </p>
          )}
          <div><Label>Numero WhatsApp</Label><Input value={to} onChange={(event) => setTo(event.target.value)} placeholder="+39…" /></div>
          {info.variables.map((number) => (
            <div key={number}>
              <Label>{`Variabile {{${number}}}`}</Label>
              <Input value={values[number] || ""} onChange={(event) => setValues((current) => ({ ...current, [number]: event.target.value }))} />
            </div>
          ))}
          <Button onClick={send} disabled={sending || info.unsupported.length > 0}>
            {sending ? "Invio…" : "Invia messaggio di prova"}
          </Button>
        </>
      )}
      {!approved.length && <p className="text-slate-500">Nessun template approvato da inviare.</p>}
    </div>
  );
}
